/**
 * Phase 46 (Plan 46-07) — REST client for notification-routes CRUD.
 *
 * Single file owns both project-scoped routes (consumed by Plan 46-08 per-
 * project Notifications tab) and global-scoped routes (consumed by THIS
 * plan's GlobalNotificationRoutesPage). Mirrors `coverageEntries.ts` shape
 * verbatim with substitutions.
 *
 * Backend contracts:
 *   - GET    /api/projects/:projectId/notification-routes      (admin or PM)
 *   - POST   /api/projects/:projectId/notification-routes      (admin or PM)
 *   - PATCH  /api/projects/:projectId/notification-routes/:id  (admin or PM)
 *   - DELETE /api/projects/:projectId/notification-routes/:id  (admin or PM)
 *   - GET    /api/admin/notification-routes                    (admin)
 *   - POST   /api/admin/notification-routes                    (admin)
 *   - PATCH  /api/admin/notification-routes/:id                (admin)
 *   - DELETE /api/admin/notification-routes/:id                (admin)
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './client';
import type { Channel, EventKind } from '../../constants/notificationEnums';

// ---------------------------------------------------------------------------
// DTO + input types — mirror backend service exports verbatim
// ---------------------------------------------------------------------------

export interface ProjectNotificationRouteDTO {
  id: number;
  projectId: number;
  eventKind: EventKind;
  channel: Channel;
  target: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GlobalNotificationRouteDTO {
  id: number;
  eventKind: EventKind;
  channel: Channel;
  target: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectNotificationRouteInput {
  eventKind: EventKind;
  channel: Channel;
  target: string | null;
}

export interface UpdateProjectNotificationRouteInput {
  channel?: Channel;
  target?: string | null;
}

export interface CreateGlobalNotificationRouteInput {
  eventKind: EventKind;
  channel: Channel;
  target: string | null;
}

export interface UpdateGlobalNotificationRouteInput {
  channel?: Channel;
  target?: string | null;
}

// ---------------------------------------------------------------------------
// Project-scoped CRUD (consumed by Plan 46-08 ProjectNotificationsTab)
// ---------------------------------------------------------------------------

export async function fetchProjectNotificationRoutes(
  projectId: number,
): Promise<ProjectNotificationRouteDTO[]> {
  const res = await apiGet<{ data: ProjectNotificationRouteDTO[] }>(
    `/projects/${projectId}/notification-routes`,
  );
  return res.data;
}

export async function createProjectNotificationRoute(
  projectId: number,
  input: CreateProjectNotificationRouteInput,
): Promise<ProjectNotificationRouteDTO> {
  const res = await apiPost<{ data: ProjectNotificationRouteDTO }>(
    `/projects/${projectId}/notification-routes`,
    input,
  );
  return res.data;
}

export async function updateProjectNotificationRoute(
  projectId: number,
  routeId: number,
  input: UpdateProjectNotificationRouteInput,
): Promise<ProjectNotificationRouteDTO> {
  const res = await apiPatch<{ data: ProjectNotificationRouteDTO }>(
    `/projects/${projectId}/notification-routes/${routeId}`,
    input,
  );
  return res.data;
}

export async function deleteProjectNotificationRoute(
  projectId: number,
  routeId: number,
): Promise<void> {
  await apiDelete<void>(`/projects/${projectId}/notification-routes/${routeId}`);
}

// ---------------------------------------------------------------------------
// Global-scoped CRUD (consumed by THIS plan's GlobalNotificationRoutesPage)
// ---------------------------------------------------------------------------

export async function fetchGlobalNotificationRoutes(): Promise<
  GlobalNotificationRouteDTO[]
> {
  const res = await apiGet<{ data: GlobalNotificationRouteDTO[] }>(
    '/admin/notification-routes',
  );
  return res.data;
}

export async function createGlobalNotificationRoute(
  input: CreateGlobalNotificationRouteInput,
): Promise<GlobalNotificationRouteDTO> {
  const res = await apiPost<{ data: GlobalNotificationRouteDTO }>(
    '/admin/notification-routes',
    input,
  );
  return res.data;
}

export async function updateGlobalNotificationRoute(
  routeId: number,
  input: UpdateGlobalNotificationRouteInput,
): Promise<GlobalNotificationRouteDTO> {
  const res = await apiPatch<{ data: GlobalNotificationRouteDTO }>(
    `/admin/notification-routes/${routeId}`,
    input,
  );
  return res.data;
}

export async function deleteGlobalNotificationRoute(
  routeId: number,
): Promise<void> {
  await apiDelete<void>(`/admin/notification-routes/${routeId}`);
}
